import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Pagination,
  PaginationItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { CaretLeft, CaretRight, Info, X } from "@phosphor-icons/react";

import warningSvg from "assets/warning.svg";
import useGetListOfGoals from "hooks/api/goals/useGetListOfGoals";

import { getAssessmentDefinition } from "../assessmentConfig";
import { panelHeader, space3, space5, space7 } from "../styled";
import { formatAssessmentDate, formatAssessmentTimestampLocal } from "../utils";
import {
  AddDocumentLinkItem,
  DocumentLinkItem,
  HistoryItem,
  type HistoryItemType,
} from "./RowItems";
import AddAssessmentDocumentModal from "./AddAssessmentDocumentModal";
import type {
  AssessmentRecord,
  AssessmentResultField,
  AssessmentResultSection,
  AssessmentType,
} from "../types";

interface AssessmentResultsProps {
  onClosePanel: () => void;
  assessmentType: AssessmentType;
  dataAssessments: AssessmentRecord[];
  clientId: string | number | null;
  currentUserId?: string | number | null;
}

const NOT_TESTED_LABEL = "Not tested";

const VINELAND_ADAPTIVE_LEVEL_KEYS = new Set([
  "abc_adaptive_level",
  "communication_adaptive_level",
  "daily_living_adaptive_level",
  "socialization_adaptive_level",
]);

const VINELAND_QUALITATIVE_DESCRIPTORS = ["Clinically Significant", "Elevated", "Average"];

const VINELAND_DESCRIPTOR_SCORE_KEYS = new Set([
  "behavior_externalizing_v_scale",
  "behavior_internalizing_v_scale",
]);

function translateVinelandComputedValue(t: TFunction, key: string, computed: string): string {
  if (VINELAND_ADAPTIVE_LEVEL_KEYS.has(key)) {
    return t(`clientHeader:options.vinelandLevel.${computed}`, { defaultValue: computed });
  }
  if (VINELAND_DESCRIPTOR_SCORE_KEYS.has(key)) {
    const descriptor = VINELAND_QUALITATIVE_DESCRIPTORS.find((d) => computed.startsWith(d));
    if (descriptor) {
      const rest = computed.slice(descriptor.length);
      return `${t(`clientHeader:options.vinelandDescriptor.${descriptor}`, { defaultValue: descriptor })}${rest}`;
    }
  }
  return computed;
}

function readValue(
  data: AssessmentRecord["data"],
  field: AssessmentResultField,
  t: TFunction
): string | number {
  const notTestedLabel = t("clientHeader:assessments.notTested", { defaultValue: NOT_TESTED_LABEL });
  if (field.compute) {
    const computed = field.compute(data);
    if (computed === null || computed === undefined) return notTestedLabel;
    return typeof computed === "string"
      ? translateVinelandComputedValue(t, field.key, computed)
      : computed;
  }
  const value = data[field.key];
  return value ?? notTestedLabel;
}

function shouldShowTooltip(value: string | number, field: AssessmentResultField, t: TFunction): boolean {
  if (!field.tooltip) return false;
  const notTestedLabel = t("clientHeader:assessments.notTested", { defaultValue: NOT_TESTED_LABEL });
  if (value === NOT_TESTED_LABEL || value === notTestedLabel || value === null || value === undefined) {
    return false;
  }
  const numericValue = typeof value === "number" ? value : parseFloat(String(value));
  if (isNaN(numericValue)) return false;
  return numericValue <= field.tooltip.threshold;
}

function getFallbackSections(data: AssessmentRecord["data"]): AssessmentResultSection[] {
  const fields = Object.keys(data)
    .filter((key) => !key.includes("date") && !key.includes("type") && key !== "Examiner")
    .map((key) => ({
      key,
      label: key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
    }));

  return fields.length ? [{ title: "Scores", fields }] : [];
}

/**
 * Converts a PascalCase enum string to a space-separated title string.
 * e.g. "VeryMild" → "Very Mild", "MaternalGrandmother" → "Maternal Grandmother"
 */
function formatEnumValue(value: string): string {
  if (value === "-") return value;
  return value.replace(/([A-Z])/g, " $1").trim();
}

type FooterRow =
  | { type: HistoryItemType; value: string }
  | { type: "documentLink"; label: string; documentId: number }
  | { type: "addDocument"; label: string; documentType: string; documentFieldName: string };

/**
 * Assessment types that show a document row in the results footer regardless of
 * whether a document has been uploaded yet.
 */
const ASSESSMENT_DOCUMENT_CONFIG: Partial<
  Record<
    AssessmentType,
    { dataField: string; label: string; documentType: string; documentFieldName: string }
  >
> = {
  vbmappBarriers: {
    dataField: "barriers_grid_document_id",
    label: "VB-MAPP Barriers Grid",
    documentType: "VB_MAPP_BARRIERS_GRID",
    documentFieldName: "barriersGridDocumentId",
  },
  vbmappTransition: {
    dataField: "transition_grid_document_id",
    label: "VB-MAPP Transition Grid",
    documentType: "VB_MAPP_TRANSITION_GRID",
    documentFieldName: "transitionGridDocumentId",
  },
  sociallySavvy: {
    dataField: "scoring_grid_document_id",
    label: "Socially Savvy Scoring Grid",
    documentType: "SOCIALLY_SAVVY_SCORING_GRID",
    documentFieldName: "scoringGridDocumentId",
  },
  efl: {
    dataField: "scoring_grid_document_id",
    label: "Essentials For Living Quick Assessment Grid",
    documentType: "EFL_SCORING_GRID",
    documentFieldName: "scoringGridDocumentId",
  },
  afls: {
    dataField: "afls_scoring_grid_document_id",
    label: "AFLS Scoring Grid",
    documentType: "AFLS_SCORING_GRID",
    documentFieldName: "aflsScoringGridDocumentId",
  },
};

function buildFooterRows(
  assessmentType: AssessmentType,
  currentAssessment: AssessmentRecord,
  administrationDate: string,
  createdDate: string,
  modifiedDate: string
): FooterRow[] {
  const { data } = currentAssessment;

  if (assessmentType === "vineland") {
    return [
      { type: "uploadedBy", value: currentAssessment.created_by || "-" },
      { type: "uploadedDate", value: createdDate },
      { type: "modifiedBy", value: currentAssessment.last_updated_by || "-" },
      { type: "modifiedOn", value: modifiedDate },
    ];
  }

  const rows: FooterRow[] = [{ type: "administrationDate", value: administrationDate }];

  if (assessmentType !== "cfql2") {
    rows.push({ type: "administratorName", value: currentAssessment.created_by || "-" });
  }

  if (assessmentType === "qabf") {
    rows.push({ type: "respondentName", value: String(data.respondent_name || "-") });
  }

  if (assessmentType === "srs2") {
    rows.push({ type: "respondentName", value: String(data.respondent_name || "-") });
    rows.push({ type: "relationshipToClient", value: String(data.relationship_to_client || "-") });
    rows.push({ type: "formAdministered", value: String(data.form_administered || "-") });
  }

  if (assessmentType === "cfql2") {
    rows.push({ type: "respondentName", value: String(data.respondent_name || "-") });
    rows.push({
      type: "relationshipToClient",
      value: formatEnumValue(String(data.relationship || "-")),
    });
    rows.push({
      type: "otherRelationshipToClient",
      value: formatEnumValue(String(data.other_relationship || "-")),
    });
    rows.push({
      type: "reportedSymptomSeverity",
      value: formatEnumValue(String(data.reported_symptom_severity || "-")),
    });
  }

  if (assessmentType === "vbmappMilestones") {
    const docId = data.milestones_grid_document_id;
    if (typeof docId === "number") {
      rows.push({ type: "documentLink", label: "VB-MAPP Milestones Grid", documentId: docId });
    }
  }

  if (assessmentType === "sociallySavvy") {
    const observationSettings = String(data.observation_settings ?? "-");
    rows.push({ type: "observationSettings", value: observationSettings });

    const durationValue = data.observation_duration_value;
    const durationUnit = data.observation_duration_unit ?? "";
    const lengthValue = durationValue != null ? `${durationValue} ${durationUnit}`.trim() : "-";
    rows.push({ type: "lengthOfObservation", value: lengthValue });
  }

  const docConfig = ASSESSMENT_DOCUMENT_CONFIG[assessmentType];
  if (docConfig) {
    const docId = data[docConfig.dataField];
    if (typeof docId === "number") {
      rows.push({ type: "documentLink", label: docConfig.label, documentId: docId });
    } else {
      rows.push({
        type: "addDocument",
        label: docConfig.label,
        documentType: docConfig.documentType,
        documentFieldName: docConfig.documentFieldName,
      });
    }
  }

  if (assessmentType === "peak") {
    const peakDocs: Array<{ key: string; label: string }> = [
      { key: "dt_module_triangle_document_id", label: "PEAK-DT Module Triangle" },
      { key: "g_module_triangle_document_id", label: "PEAK-G Module Triangle" },
      { key: "e_module_triangle_document_id", label: "PEAK-E Module Triangle" },
      { key: "t_module_triangle_document_id", label: "PEAK-T Module Triangle" },
    ];
    for (const { key, label } of peakDocs) {
      const docId = data[key];
      if (typeof docId === "number") {
        rows.push({ type: "documentLink", label, documentId: docId });
      }
    }
  }

  if (assessmentType === "cpfq") {
    // Priority: FillOut PDF (webhook-uploaded) > combined grid (manually uploaded) > add-grid link.
    const filloutPdfId = data.fillout_pdf_document_id;
    if (typeof filloutPdfId === "number") {
      rows.push({ type: "documentLink", label: "FillOut PDF", documentId: filloutPdfId });
    } else {
      const gridDocId = data.child_report_grid_document_id;
      if (typeof gridDocId === "number") {
        rows.push({
          type: "documentLink",
          label: "CPFQ Report Grid",
          documentId: gridDocId,
        });
      } else {
        rows.push({
          type: "addDocument",
          label: "CPFQ Report Grid",
          documentType: "CPFQ_CHILD_REPORT_GRID",
          documentFieldName: "childReportGridDocumentId",
        });
      }
    }
  }

  // FillOut PDF — shown as a read-only document link when the webhook has attached it.
  // (cpfq handles its own FillOut PDF inline above — excluded here.)
  if (["cfql2", "qabf", "efl", "sociallySavvy"].includes(assessmentType)) {
    const pdfId = data.fillout_pdf_document_id;
    if (typeof pdfId === "number") {
      rows.push({ type: "documentLink", label: "FillOut PDF", documentId: pdfId });
    }
  }

  return rows;
}

export default function AssessmentResults({
  onClosePanel,
  assessmentType,
  dataAssessments,
  clientId,
  currentUserId,
}: AssessmentResultsProps) {
  const { t } = useTranslation(["clientHeader"]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [addDocModal, setAddDocModal] = useState<{
    documentLabel: string;
    documentType: string;
    assessmentId: string | number;
    assessmentType: AssessmentType;
    documentFieldName: string;
  } | null>(null);
  const currentAssessment = dataAssessments[selectedPage - 1] ?? dataAssessments[0];
  const definition = getAssessmentDefinition(assessmentType);

  // QABF stores the behavior reduction goal by id — resolve it to the goal title for display.
  // Archived goals are included so older assessments still resolve.
  const { goals } = useGetListOfGoals(
    assessmentType === "qabf" && clientId ? String(clientId) : "",
    { includeArchived: true }
  );

  const assessmentData = useMemo(() => {
    const data = currentAssessment?.data;
    const goalId = data?.behavior_reduction_goal;
    if (!data || goalId === null || goalId === undefined || goalId === "") return data;
    const goal = goals.find((g: { id: number }) => String(g.id) === String(goalId));
    return goal ? { ...data, behavior_reduction_goal: goal.title } : data;
  }, [currentAssessment, goals]);

  const sections = useMemo(() => {
    if (!currentAssessment) {
      return [];
    }

    return definition.resultSections.length
      ? definition.resultSections
      : getFallbackSections(currentAssessment.data);
  }, [currentAssessment, definition.resultSections]);

  if (!currentAssessment) {
    return (
      <Stack component="section" rowGap={space5}>
        <Stack component="header" direction="row" justifyContent="space-between">
          <Typography component="h3" variant="subtitle1">
            {definition.title}
          </Typography>

          <IconButton onClick={onClosePanel} size="small" aria-label="Close results panel">
            <X weight="regular" size={20} />
          </IconButton>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t("clientHeader:assessments.noResultsYet")}
        </Typography>
      </Stack>
    );
  }

  const administrationDate = formatAssessmentDate(currentAssessment.data.administration_date);
  const createdDate = formatAssessmentTimestampLocal(currentAssessment.created);
  const modifiedDate = formatAssessmentTimestampLocal(currentAssessment.last_updated);
  const primarySection = sections[0];
  const heroField = !definition.skipHero ? primarySection?.fields[0] : undefined;
  const heroValue = heroField ? readValue(assessmentData, heroField, t) : "-";
  const heroLabel = heroField
    ? t(`clientHeader:assessments.fields.${heroField.key}`, { defaultValue: heroField.label })
    : definition.title;
  const footerRows = buildFooterRows(
    assessmentType,
    currentAssessment,
    administrationDate,
    createdDate,
    modifiedDate
  );

  return (
    <>
      <Stack component="header" direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          <Pagination
            count={Math.max(dataAssessments.length, 1)}
            disabled={dataAssessments.length <= 1}
            variant="outlined"
            shape="rounded"
            sx={panelHeader}
            page={selectedPage}
            onChange={(_event, page) => {
              setSelectedPage(page);
            }}
            renderItem={(item) => (
              <PaginationItem slots={{ previous: CaretLeft, next: CaretRight }} {...item} />
            )}
          />

          <Typography component="h3" variant="h2" marginLeft={space3}>
            {administrationDate}
          </Typography>
        </Stack>

        <IconButton onClick={onClosePanel} size="small" aria-label="Close results panel">
          <X weight="regular" size={20} />
        </IconButton>
      </Stack>

      {!definition.skipHero && (
        <Stack marginTop={space5}>
          <Typography component="h2" variant="h1">
            {heroValue}
          </Typography>
          <Typography variant="h3">{heroLabel}</Typography>
        </Stack>
      )}

      <Stack marginTop={space5} rowGap={space7}>
        {sections.map((section, sectionIndex) => {
          const isSectionHero =
            sectionIndex > 0 && !!section.showSectionHero && section.fields.length > 0;
          const sectionHeroField = isSectionHero ? section.fields[0] : undefined;

          return (
            <Box key={section.title || String(sectionIndex)}>
              {section.title && !isSectionHero && (
                <Typography component="h3" variant="subtitle2" marginBottom={space3}>
                  {t(`clientHeader:assessments.sections.${section.title}`, {
                    defaultValue: section.title,
                  })}
                </Typography>
              )}

              {isSectionHero && sectionHeroField && (
                <Stack marginBottom={space3}>
                  <Typography component="h2" variant="h1">
                    {readValue(assessmentData, sectionHeroField, t)}
                  </Typography>
                  <Typography variant="h3">
                    {t(`clientHeader:assessments.fields.${sectionHeroField.key}`, {
                      defaultValue: sectionHeroField.label,
                    })}
                  </Typography>
                </Stack>
              )}

              <Grid container>
                {section.fields
                  .filter(
                    (field) =>
                      !(sectionIndex === 0 && heroField && field.key === heroField.key) &&
                      !(isSectionHero && sectionHeroField && field.key === sectionHeroField.key)
                  )
                  .map((field) => {
                    const fieldValue = readValue(assessmentData, field, t);
                    const showTooltip = shouldShowTooltip(fieldValue, field, t);

                    return (
                      <React.Fragment key={field.key}>
                        <Grid item xs={8}>
                          <Typography variant="body2">
                            {t(`clientHeader:assessments.fields.${field.key}`, {
                              defaultValue: field.label,
                            })}
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={1}
                          >
                            {showTooltip && field.tooltip && (
                              <Tooltip
                                title={`Recommendation: ${field.tooltip.text}`}
                                placement="left"
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  {assessmentType === "cfql2" ? (
                                    <img
                                      src={warningSvg}
                                      alt="warning"
                                      style={{ width: 16, height: 16 }}
                                    />
                                  ) : (
                                    <Info
                                      size={16}
                                      weight="fill"
                                      color="var(--mui-palette-info-main)"
                                    />
                                  )}
                                </Box>
                              </Tooltip>
                            )}
                            <Typography variant="subtitle2" align="right">
                              {fieldValue}
                            </Typography>
                          </Stack>
                        </Grid>
                      </React.Fragment>
                    );
                  })}
              </Grid>
            </Box>
          );
        })}
      </Stack>

      <Divider sx={{ marginTop: space7 }} />

      <Grid container marginY={space7} rowGap={space5}>
        {footerRows.map((row, index) =>
          row.type === "documentLink" ? (
            currentUserId != null ? (
              <DocumentLinkItem
                key={`doc-${row.label}-${index}`}
                label={row.label}
                documentId={row.documentId}
                currentUserId={currentUserId}
              />
            ) : null
          ) : row.type === "addDocument" ? (
            <AddDocumentLinkItem
              key={`add-doc-${row.label}-${index}`}
              label={row.label}
              onClick={() =>
                setAddDocModal({
                  documentLabel: row.label,
                  documentType: row.documentType,
                  assessmentId: currentAssessment.id!,
                  assessmentType,
                  documentFieldName: row.documentFieldName,
                })
              }
            />
          ) : (
            <HistoryItem key={`${row.type}-${index}`} type={row.type} value={row.value} />
          )
        )}
      </Grid>

      {addDocModal && clientId != null && (
        <AddAssessmentDocumentModal
          documentLabel={addDocModal.documentLabel}
          documentType={addDocModal.documentType}
          clientId={clientId}
          assessmentId={addDocModal.assessmentId}
          assessmentType={addDocModal.assessmentType}
          documentFieldName={addDocModal.documentFieldName}
          onClose={() => setAddDocModal(null)}
        />
      )}
    </>
  );
}
